<template>
  <div class="card card-graph">
    <div class="card-header d-flex justify-content-between">
      <h5 class="card-title">{{ title }}</h5>
      <router-link
        v-if="historyLink"
        :to="{ name: 'analytics-dashboard-component', params: { deviceId: devId, componentname: 'device-role' } }"
        class="btn btn-primary btn-sm d-flex align-items-center gap-1"
      >
        <i class="bx bx-history"></i>
        History
      </router-link>
    </div>
    <div class="card-body">
      <div class="graph d-flex flex-wrap flex-col justify-content-center" ref="graphRef">
        <div class="d-flex flex-col justify-content-center" ref="charRef" style="height: 290px; width: 100%"></div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.graph {
  min-width: 200px;
  align-items: top;
}
</style>

<script>
import { onMounted, onUnmounted, onBeforeMount, onBeforeUnmount, ref, computed, watch } from 'vue'
import axios from 'axios'
import * as echarts from 'echarts'
import { useStore } from 'vuex'
import { createHistoryQuery, roleLabel } from '@/data/createRoleQuery'
import { getNoDataGraphic } from '@/data/chartEmptyState'

export default {
  name: 'GraphDeviceRoleHistory',
  props: {
    host: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
    refresh: {
      type: Number,
      default: 15,
    },
    title: {
      type: String,
      default: 'Device Role History',
    },
    devId: {
      type: Number,
      default: 0,
    },
    historyLink: {
      type: Boolean,
      default: false,
    },
    /** Override range (e.g. dashboard "today": -1d / 0m / 15m). History page uses Vuex instead. */
    durationStart: {
      type: String,
      default: null,
    },
    durationEnd: {
      type: String,
      default: null,
    },
    durationInterval: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const timer = ref(null)
    const chart = ref(null)
    const charRef = ref(null)
    const graphRef = ref(null)
    const store = useStore()

    const layoutMode = computed(() => store.state.layout.layoutMode)
    const apiKey = computed(() => store.state.auth.user?.accessToken)
    const apiHost = computed(() => window.apiHost)

    const strDurationStart = ref(props.durationStart || '-1d')
    const strDurationEnd = ref(props.durationEnd || '0m')
    const strInterval = ref(props.durationInterval || '15m')

    const resizeObserver = new ResizeObserver(() => onWindowResize())

    watch(layoutMode, (newMode) => {
      if (chart.value !== null) {
        const theme = newMode === 'dark' ? 'dark' : 'light'
        chart.value.dispose()
        chart.value = echarts.init(charRef.value, theme)
        main()
      }
    })

    const graphUpdateCount = computed(() => store.state.graph.graphUpdateCount)
    watch(graphUpdateCount, () => {
      // History page duration tabs — skip when parent fixed the range (dashboard today view)
      if (props.durationStart) {
        return
      }
      const _strDurationStart = computed(() => store.state.graph.graphDurationStart)
      const _strDurationEnd = computed(() => store.state.graph.graphDurationEnd)
      const _strInterval = computed(() => store.state.graph.graphInterval)

      strDurationEnd.value = _strDurationEnd.value
      strDurationStart.value = _strDurationStart.value
      strInterval.value = _strInterval.value

      if (chart.value !== null) {
        chart.value.dispose()
        chart.value = null
        initChart()
      }
      chart.value.showLoading({
        text: 'Loading data, please wait...',
        color: '#4f6bed',
        textColor: '#000',
        maskColor: 'rgba(255, 255, 255, 0.2)',
        zlevel: 0,
      })
      main()
    })

    function getChartOptions(data) {
      const seriesData = []

      if (data && data.length > 0) {
        data.forEach((item) => {
          // Only exact 0 or 1 — no fractional values
          const role = Math.round(Number(item[1])) === 1 ? 1 : 0
          seriesData.push([item[0], role])
        })
      }

      const hasData = seriesData.length > 0

      return {
        graphic: hasData ? [] : getNoDataGraphic(layoutMode.value === 'dark'),
        tooltip: {
          show: hasData,
          trigger: 'axis',
          axisPointer: {
            type: 'line',
            snap: true,
            label: {
              show: false,
            },
          },
          formatter: function (params) {
            if (!params || !params.length || params[0].data == null) {
              return ''
            }
            const timestamp = params[0].data[0]
            // Force integer 0 or 1 even if smooth curve interpolates
            const value = Math.round(Number(params[0].data[1])) === 1 ? 1 : 0
            const label = roleLabel(value)
            const date = new Date(timestamp)

            const dateStr = date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })

            const timeStr = date.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: false,
            })

            return `
              <div style="padding: 5px;">
                <strong>${dateStr}</strong><br/>
                <strong>${timeStr}</strong><br/>
                <span style="color: #73bf69;">●</span> Role: <strong>${label} (${value})</strong>
              </div>
            `
          },
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#73bf69',
          borderWidth: 1,
          textStyle: {
            color: '#fff',
          },
        },
        xAxis: {
          show: hasData,
          type: 'time',
          boundaryGap: false,
          axisLabel: {
            formatter: function (value) {
              const date = new Date(value)
              return date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              })
            },
          },
        },
        yAxis: {
          show: hasData,
          type: 'value',
          name: '',
          min: 0,
          max: 1,
          interval: 1,
          axisLabel: {
            formatter: function (value) {
              if (value === 1) return 'Primary'
              if (value === 0) return 'Secondary'
              return ''
            },
          },
        },
        series: [
          {
            name: 'Device Role',
            type: 'line',
            smooth: true,
            smoothMonotone: 'x',
            symbol: 'none',
            data: seriesData,
            lineStyle: {
              color: '#73bf69',
              width: 2,
            },
            itemStyle: {
              color: '#73bf69',
            },
            areaStyle: hasData
              ? {
                  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: 'rgba(115, 191, 105, 0.45)' },
                    { offset: 1, color: 'rgba(115, 191, 105, 0.05)' },
                  ]),
                }
              : undefined,
          },
        ],
        grid: {
          left: '3%',
          right: '4%',
          bottom: '18%',
          top: '12%',
          containLabel: true,
        },
      }
    }

    async function drawChart(data) {
      if (!chart.value) return
      // Replace options fully so empty charts clear previous series
      const option = getChartOptions(data || [])
      option.backgroundColor = layoutMode.value === 'dark' ? '#22354e' : '#ffffff'
      chart.value.setOption(option, true)
      chart.value.hideLoading()
    }

    function initChart() {
      if (chart.value === null) {
        const theme = layoutMode.value === 'dark' ? 'dark' : 'light'
        chart.value = echarts.init(charRef.value, theme)
      }
      const option = getChartOptions([])
      option.backgroundColor = layoutMode.value === 'dark' ? '#22354e' : '#ffffff'
      chart.value.setOption(option)
    }

    async function main() {
      try {
        const query = createHistoryQuery(
          props.host,
          strDurationStart.value,
          strDurationEnd.value,
          strInterval.value
        )

        const response = await axios.post(apiHost.value + '/core/api/v1/analytics/query', query, {
          headers: {
            Authorization: 'Bearer ' + apiKey.value,
            'Content-Type': 'application/vnd.flux',
            Accept: 'application/csv',
          },
        })

        const data = response.data
        const roleData = []
        let fieldTime = 3
        let fieldValue = 5

        data.split('\n').forEach((line) => {
          const fields = line.trim().split(',')
          if (fields.length >= 5 && fields[1] === 'result') {
            fieldTime = fields.indexOf('_time')
            fieldValue = fields.indexOf('_value')
          } else if (fields.length >= 5 && fields[fieldTime] && fields[fieldValue]) {
            const val = parseFloat(fields[fieldValue])
            if (!isNaN(val)) {
              roleData.push([fields[fieldTime], val])
            }
          }
        })

        drawChart(roleData)
      } catch (error) {
        console.error('Error fetching device role data:', error)
        drawChart([])
      }
    }

    function onWindowResize() {
      if (chart.value !== null) chart.value.resize()
    }

    onMounted(() => {
      initChart()
      main()
      if (props.refresh > 0) {
        timer.value = setInterval(() => main(), props.refresh * 1000)
      }
      if (graphRef.value) {
        resizeObserver.observe(graphRef.value)
      }
    })

    onUnmounted(() => {
      if (props.refresh > 0 && timer.value !== null) clearInterval(timer.value)
      if (chart.value !== null) {
        chart.value.dispose()
        chart.value = null
      }
    })

    onBeforeMount(() => {
      window.addEventListener('resize', onWindowResize)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', onWindowResize)
      if (graphRef.value) {
        resizeObserver.unobserve(graphRef.value)
      }
    })

    return {
      charRef,
      graphRef,
    }
  },
}
</script>
